import { useState } from 'react'
import { AlertTriangle, ChevronDown, RefreshCw, Square, X } from 'lucide-react'
import { Image } from '../../types/image'
import { adminApiClient } from '../../api/client'
import { useBulkRefreshStore } from '../../stores/bulkRefreshStore'

interface BulkRefreshPanelProps {
  albumId: string
  /** Loaded images, used to resolve filenames for the failure list. */
  images: Image[]
}

export const BulkRefreshPanel: React.FC<BulkRefreshPanelProps> = ({ albumId, images }) => {
  const run = useBulkRefreshStore((state) => state.runs[albumId])
  const retryFailedImages = useBulkRefreshStore((state) => state.retryFailedImages)
  const stopBulkRefresh = useBulkRefreshStore((state) => state.stopBulkRefresh)
  const clearRun = useBulkRefreshStore((state) => state.clearRun)
  const [showFailed, setShowFailed] = useState(false)

  if (!run) return null

  const active = run.status === 'collecting' || run.status === 'running'
  const processed = run.done + run.failed + run.skipped
  const percent = run.total > 0 ? Math.round((processed / run.total) * 100) : 0
  const failedIds = Object.entries(run.imageStatuses)
    .filter(([, status]) => status === 'failed')
    .map(([id]) => id)
  const filenameFor = (imageId: string) =>
    images.find((image) => image.imageId === imageId)?.filename || imageId

  const statusLabel =
    run.status === 'collecting'
      ? 'Collecting image list…'
      : run.status === 'running'
        ? 'Refreshing thumbnails…'
        : run.status === 'stopped'
          ? 'Stopped'
          : run.status === 'failed'
            ? 'Failed'
            : 'Complete'

  const statusColor =
    run.status === 'failed'
      ? 'text-red-600'
      : run.status === 'stopped'
        ? 'text-amber-600'
        : run.status === 'completed'
          ? 'text-green-600'
          : 'text-gray-900'

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6" data-testid="bulk-refresh-panel">
      <div className="flex items-center justify-between gap-4 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          {active && <RefreshCw className="w-4 h-4 text-orange-600 animate-spin shrink-0" />}
          {run.status === 'failed' && <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />}
          <span className={`font-medium ${statusColor}`}>{statusLabel}</span>
          {run.total > 0 && (
            <span className="text-sm text-gray-500 truncate">
              {processed} of {run.total} images
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {active && (
            <button
              onClick={() => stopBulkRefresh(albumId)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              <Square className="w-3 h-3" />
              Stop
            </button>
          )}
          {!active && failedIds.length > 0 && (
            <button
              onClick={() => retryFailedImages(adminApiClient, albumId)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700"
            >
              <RefreshCw className="w-3 h-3" />
              Retry failed ({failedIds.length})
            </button>
          )}
          {!active && (
            <button
              onClick={() => clearRun(albumId)}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              aria-label="Dismiss refresh status"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {run.total > 0 && (
        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mb-3">
          <div
            className="h-full bg-orange-500 rounded-full transition-all duration-300"
            style={{ width: `${percent}%` }}
          />
        </div>
      )}

      <div className="flex items-center gap-4 text-sm text-gray-500">
        <span className="text-green-600">{run.done} done</span>
        {run.failed > 0 && <span className="text-red-600">{run.failed} failed</span>}
        {run.skipped > 0 && <span>{run.skipped} skipped</span>}
        {run.runningIds.length > 0 && (
          <span className="text-orange-600">{run.runningIds.length} in flight</span>
        )}
      </div>

      {run.error && (
        <p className="mt-2 text-sm text-red-600">{run.error}</p>
      )}

      {!active && failedIds.length > 0 && (
        <div className="mt-3">
          <button
            onClick={() => setShowFailed((prev) => !prev)}
            className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
          >
            <ChevronDown className={`w-4 h-4 transition-transform ${showFailed ? 'rotate-180' : ''}`} />
            {showFailed ? 'Hide' : 'Show'} failed images ({failedIds.length})
          </button>
          {showFailed && (
            <ul className="mt-2 space-y-1 text-sm text-gray-600 max-h-40 overflow-y-auto">
              {failedIds.map((id) => (
                <li key={id} className="flex items-center gap-2">
                  <AlertTriangle className="w-3 h-3 text-red-500 shrink-0" />
                  <span className="truncate">{filenameFor(id)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
